/**
 * WebLLMAdapter — a real LLM running IN THE BROWSER via WebGPU (MLC WebLLM).
 *
 * This is the honest "local model": inference happens on the user's GPU, no
 * network call for the model itself (weights are fetched once from a CDN and
 * cached in the browser). It implements the public @macrokit/runtime
 * `LLMAdapter` contract, so the SDK's IntentRouter drives it exactly like any
 * other adapter — the browser runs ONLY the runtime.
 *
 * WebLLM's chat API is OpenAI-shaped, so the message/tool mapping mirrors the
 * SDK's OpenAICompatibleAdapter. WebLLM is loaded from a CDN at runtime (kept
 * out of the bundle via a non-literal import specifier) so the static page
 * stays small and has no build-time native/wasm dependency.
 */
import type {
  ChatMessage,
  CompleteOptions,
  CompleteResult,
  FinishReason,
  LLMAdapter,
  NormalizedToolCall,
  ToolSpec,
} from "@macrokit/runtime";

// Pinned CDN build of @mlc-ai/web-llm. Assigned to a variable so esbuild does
// not try to resolve/bundle it — the browser performs a native dynamic import.
const WEBLLM_CDN = "https://esm.run/@mlc-ai/web-llm@0.2.79";

export interface WebLLMProgress {
  text: string;
  progress: number;
}

export interface WebLLMAdapterOptions {
  /** MLC model id, e.g. "Qwen2.5-1.5B-Instruct-q4f16_1-MLC". */
  model: string;
}

interface MLCEngine {
  chat: {
    completions: {
      create(req: Record<string, unknown>): Promise<ProviderCompletionResponse>;
    };
  };
}

export class WebLLMAdapter implements LLMAdapter {
  readonly provider = "webllm-local";
  readonly defaultModel: string;
  private engine: MLCEngine | null = null;
  private loading: Promise<void> | null = null;

  constructor(opts: WebLLMAdapterOptions) {
    this.defaultModel = opts.model;
  }

  get ready(): boolean {
    return this.engine !== null;
  }

  /** Returns true if this browser can run WebGPU inference at all. */
  static webgpuAvailable(): boolean {
    return typeof navigator !== "undefined" && "gpu" in navigator;
  }

  /** Download weights + spin up the engine. Idempotent. Reports progress. */
  async init(onProgress?: (p: WebLLMProgress) => void): Promise<void> {
    if (this.engine) return;
    if (this.loading) return this.loading;
    if (!WebLLMAdapter.webgpuAvailable()) {
      throw new Error(
        "This browser has no WebGPU — the in-browser model cannot run here. " +
          "Try a recent Chrome/Edge (or Safari Technology Preview). We do NOT " +
          "fall back to a cloud model.",
      );
    }
    this.loading = (async () => {
      const webllm = (await import(/* @vite-ignore */ WEBLLM_CDN)) as {
        CreateMLCEngine: (
          model: string,
          cfg: { initProgressCallback?: (r: { text: string; progress: number }) => void },
        ) => Promise<MLCEngine>;
      };
      this.engine = await webllm.CreateMLCEngine(this.defaultModel, {
        initProgressCallback: (r) => onProgress?.({ text: r.text, progress: r.progress }),
      });
    })();
    try {
      await this.loading;
    } finally {
      this.loading = null;
    }
  }

  async complete(opts: CompleteOptions): Promise<CompleteResult> {
    if (!this.engine) {
      throw new Error("WebLLM engine not initialized — call init() first.");
    }
    const req: Record<string, unknown> = {
      messages: opts.messages.map(toProviderMessage),
    };
    if (opts.tools && opts.tools.length > 0) {
      req.tools = opts.tools.map(toProviderTool);
      req.tool_choice = "auto";
    }
    req.temperature = opts.temperature ?? 0;
    if (opts.maxTokens !== undefined) req.max_tokens = opts.maxTokens;

    const payload = await this.engine.chat.completions.create(req);
    return fromProviderResponse(payload);
  }
}

// ---------------------------------------------------------------------------
// OpenAI-shaped mappers (WebLLM mirrors the OpenAI chat schema)
// ---------------------------------------------------------------------------

interface ProviderToolCall {
  id: string;
  type: "function";
  function: { name: string; arguments: string };
}
interface ProviderMessage {
  role: "system" | "user" | "assistant" | "tool";
  content: string | null;
  tool_calls?: ProviderToolCall[];
  tool_call_id?: string;
  name?: string;
}
interface ProviderCompletionResponse {
  choices: Array<{ message: ProviderMessage; finish_reason: string | null }>;
}

function toProviderMessage(m: ChatMessage): ProviderMessage {
  const base: ProviderMessage = { role: m.role, content: m.content };
  if (m.toolCalls && m.toolCalls.length > 0) {
    base.tool_calls = m.toolCalls.map((tc) => ({
      id: tc.id,
      type: "function",
      function: { name: tc.name, arguments: JSON.stringify(tc.args) },
    }));
    if (base.content === "") base.content = null;
  }
  if (m.toolCallId) base.tool_call_id = m.toolCallId;
  if (m.name) base.name = m.name;
  return base;
}

function toProviderTool(t: ToolSpec): Record<string, unknown> {
  return { type: "function", function: { name: t.name, description: t.description, parameters: t.parameters } };
}

function fromProviderResponse(payload: ProviderCompletionResponse): CompleteResult {
  const choice = payload.choices?.[0];
  if (!choice) return { text: "", toolCalls: [], finishReason: "error", raw: payload };
  const msg = choice.message ?? { role: "assistant", content: "" };

  let toolCalls: NormalizedToolCall[] = (msg.tool_calls ?? []).map((tc) => ({
    id: tc.id,
    name: tc.function.name,
    args: safeParseJson(tc.function.arguments),
  }));

  // Small models sometimes emit a tool call as JSON in the content instead of
  // the structured tool_calls field. Best-effort recover one.
  if (toolCalls.length === 0 && msg.content) {
    const recovered = recoverToolCallFromText(msg.content);
    if (recovered) toolCalls = [recovered];
  }

  return {
    text: msg.content ?? "",
    toolCalls,
    finishReason: normalizeFinishReason(choice.finish_reason),
    raw: payload,
  };
}

function normalizeFinishReason(reason: string | null): FinishReason {
  switch (reason) {
    case "stop":
    case "tool_calls":
    case "length":
    case "content_filter":
      return reason;
    default:
      return "unknown";
  }
}

function safeParseJson(s: string): Record<string, unknown> {
  if (!s) return {};
  try {
    const parsed = JSON.parse(s);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/** Parse a `{"name": "...", "arguments": {...}}`-style blob from free text. */
function recoverToolCallFromText(text: string): NormalizedToolCall | null {
  const match = text.match(/\{[\s\S]*\}/);
  if (!match) return null;
  try {
    const obj = JSON.parse(match[0]) as Record<string, unknown>;
    const name = (obj.name ?? obj.tool ?? (obj.function as { name?: string })?.name) as string | undefined;
    if (!name || typeof name !== "string") return null;
    const args =
      (obj.arguments as Record<string, unknown>) ??
      (obj.args as Record<string, unknown>) ??
      (obj.parameters as Record<string, unknown>) ??
      {};
    return { id: `recovered_${name}`, name, args: typeof args === "object" ? args : {} };
  } catch {
    return null;
  }
}
